import api from './axios';

export type PromoStatus = 'live' | 'scheduled' | 'disabled' | 'ended';
export type PromoStatusFilter = 'current' | 'live' | 'scheduled' | 'ended' | 'disabled' | 'all';
export type BonusRounding = 'down' | 'up' | 'nearest';

export interface PlatformPromotion {
  id: number;
  tenant_id: number;
  platform_id: number;
  platform_name: string;
  platform_display_name: string;
  name: string | null;
  bonus_percentage: number;
  max_bonus_per_load: number | null;
  starts_at: string;
  ends_at: string | null; // null = runs until ended
  is_enabled: boolean;
  status: PromoStatus; // computed by the server; prefer it over client clock math
  stats: { loads: number; loaded_amount: number; bonus_paid: number };
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformPromotionInput {
  platform_id: number;
  bonus_percentage: number;
  name?: string | null;
  max_bonus_per_load?: number | null;
  starts_at?: string;
  ends_at?: string | null;
  is_enabled?: boolean;
}

export interface PromotablePlatform {
  id: number;
  name: string;
  display_name: string;
}

const base = (tenantId: number) => `/api/v1/admin/tenants/${tenantId}/platform-promotions`;

export const platformPromotionsApi = {
  platforms: async (tenantId: number): Promise<PromotablePlatform[]> =>
    (await api.get<any>(`${base(tenantId)}/platforms`)).data.platforms,
  list: async (tenantId: number, status: PromoStatusFilter = 'current', platformId?: number): Promise<PlatformPromotion[]> =>
    (await api.get<any>(base(tenantId), { params: { status, platform_id: platformId } })).data.promotions,
  create: async (tenantId: number, data: PlatformPromotionInput): Promise<PlatformPromotion> =>
    (await api.post<any>(base(tenantId), data)).data.promotion,
  update: async (tenantId: number, id: number, data: Partial<PlatformPromotionInput>): Promise<PlatformPromotion> =>
    (await api.put<any>(`${base(tenantId)}/${id}`, data)).data.promotion,
  end: async (tenantId: number, id: number): Promise<PlatformPromotion> =>
    (await api.post<any>(`${base(tenantId)}/${id}/end`)).data.promotion,
  remove: async (tenantId: number, id: number) => api.delete(`${base(tenantId)}/${id}`),
  // Tenant bonus rounding, used for the dialog's live preview. Defaults to "down".
  bonusRounding: async (tenantId: number): Promise<BonusRounding> => {
    const configs = (await api.get<any>('/api/v1/admin/tenant-configs/')).data as { tenant_id: number; bonus_rounding?: string }[];
    const config = Array.isArray(configs) ? configs.find((c) => c.tenant_id === tenantId) : undefined;
    const rounding = config?.bonus_rounding;
    return rounding === 'up' || rounding === 'nearest' ? rounding : 'down';
  },
};

// FastAPI errors: { detail: string } or, for 422, { detail: [{ msg }] }.
export const getApiErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

// Mirrors the server: round the raw bonus the tenant's way, then apply the per-load cap.
export const computePromoBonus = (
  amount: number,
  pct: number,
  cap: number | null,
  rounding: BonusRounding = 'down',
): number => {
  const raw = (amount * pct) / 100;
  const rounded = rounding === 'up' ? Math.ceil(raw) : rounding === 'nearest' ? Math.round(raw) : Math.floor(raw);
  const bonus = cap != null ? Math.min(rounded, cap) : rounded;
  return Math.max(bonus, 0);
};
